import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  Divider,
  Box,
  Icon,
  Image,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Flex,
  Badge,
  Progress,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Select,
  IconButton,
  Tooltip,
  Spinner
} from '@chakra-ui/react';
import { FaUser, FaExclamationTriangle, FaCrown, FaUpload, FaLock, FaCropAlt, FaSearchPlus, FaShieldAlt, FaBan, FaTimesCircle } from 'react-icons/fa';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../../utils/cropImage';
import { useSelector, useDispatch } from 'react-redux';
import { updateUserBio, updateUserState, deleteAccount, uploadCustomAvatar, getPrivacySettings, updatePrivacySettings, unblockUser } from '../../Redux/Login/action';
import AvatarSelector from './AvatarSelector';
import { US_STATES } from '../Shared/StateSelector';

const MembershipSettings = ({ isOpen, onClose }) => {
  const [bio, setBio] = useState('');
  const [bioInitialized, setBioInitialized] = useState(false);
  const [isSavingBio, setIsSavingBio] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [stateInitialized, setStateInitialized] = useState(false);
  const [isSavingState, setIsSavingState] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [customAvatarFile, setCustomAvatarFile] = useState(null);
  const [customAvatarPreview, setCustomAvatarPreview] = useState(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Crop state
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const maxBioChars = 500;

  // Phase 6: Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    bioVisibility: 'public',
    onlineStatusVisibility: 'community',
    kinListVisibility: 'kin_only',
    messagingGate: 'community'
  });
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacyLoaded, setPrivacyLoaded] = useState(false);

  const toast = useToast();
  const dispatch = useDispatch();
  const profile = useSelector(state => state.auth.profile);
  const profileLoading = useSelector(state => state.auth.profileLoading);
  const isVip = profile?.membershipTier === 'vip';

  // Phase 6: Load privacy settings when modal opens
  useEffect(() => {
    if (isOpen && !privacyLoaded) {
      loadPrivacySettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  // Phase 1C: Admin check kept for safety — but with sidecar model, admin's User doc
  // should have membershipTier='vip' making isVip true directly. This is a fallback.
  const isAdmin = profile?.adminLevel === 'admin' || profile?.adminLevel === 'owner' || profile?.adminLevel === 'senior-admin';

  // Avatar selector disclosure (opened from Avatar tab)
  const avatarSelector = useDisclosure();

  // Leave-assembly confirmation dialog
  const {
    isOpen: isLeaveDialogOpen,
    onOpen: onOpenLeaveDialog,
    onClose: onCloseLeaveDialog
  } = useDisclosure();
  const cancelRef = React.useRef();

  // Seed bio from profile when modal opens (once per open)
  const handleModalOpen = () => {
    if (!bioInitialized) {
      setBio(profile.userBio || '');
      setBioInitialized(true);
    }
    if (!stateInitialized) {
      setSelectedState(profile.userState || profile.state || '');
      setStateInitialized(true);
    }
    // Phase 6: Load privacy settings
    if (!privacyLoaded) {
      loadPrivacySettings();
    }
  };

  // Phase 6: Load privacy settings from server
  const loadPrivacySettings = async () => {
    setPrivacyLoading(true);
    try {
      const result = await dispatch(getPrivacySettings());
      if (result.success) {
        setPrivacySettings(result.privacySettings);
        setBlockedUsers(result.blockedUsers || []);
        setPrivacyLoaded(true);
      }
    } catch (err) {
      console.error('Failed to load privacy settings:', err);
    } finally {
      setPrivacyLoading(false);
    }
  };

  // Phase 6: Save privacy settings
  const handleSavePrivacy = async () => {
    setPrivacySaving(true);
    try {
      const result = await dispatch(updatePrivacySettings(privacySettings));
      if (result.success) {
        toast({ title: 'Privacy settings updated', status: 'success', duration: 3000 });
      } else {
        throw new Error(result.error || 'Failed to save');
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setPrivacySaving(false);
    }
  };

  // Phase 6: Unblock a user
  const handleUnblock = async (burrowerId) => {
    try {
      const result = await dispatch(unblockUser(burrowerId));
      if (result.success) {
        setBlockedUsers(prev => prev.filter(id => id !== burrowerId));
        toast({ title: `${burrowerId} unblocked`, status: 'info', duration: 3000 });
      } else {
        throw new Error(result.error || 'Failed to unblock');
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const handleModalClose = () => {
    setBioInitialized(false);
    setStateInitialized(false);
    setPassword('');
    setConfirmation('');
    setCustomAvatarFile(null);
    setCustomAvatarPreview(null);
    setShowCropper(false);
    setCropImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setPrivacyLoaded(false);
    onClose();
  };

  const handleAvatarFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Please select a JPEG, PNG, WebP, or GIF image.', status: 'error', duration: 4000 });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Avatar must be under 2 MB.', status: 'error', duration: 4000 });
      return;
    }

    // Open crop UI instead of going straight to preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropImageSrc(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleCropConfirm = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;
    try {
      const croppedBlob = await getCroppedImg(cropImageSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], 'avatar-cropped.png', { type: 'image/png' });
      setCustomAvatarFile(croppedFile);
      setCustomAvatarPreview(URL.createObjectURL(croppedBlob));
      setShowCropper(false);
      setCropImageSrc(null);
    } catch (err) {
      toast({ title: 'Crop failed', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setCropImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleUploadCustomAvatar = async () => {
    if (!customAvatarFile) return;
    setIsUploadingAvatar(true);
    try {
      const result = await dispatch(uploadCustomAvatar(customAvatarFile));
      if (result.success) {
        toast({ title: 'Profile image uploaded!', description: 'Your new profile image is now active.', status: 'success', duration: 3000 });
        setCustomAvatarFile(null);
        setCustomAvatarPreview(null);
      } else {
        toast({ title: 'Upload failed', description: result.error || 'Please try again.', status: 'error', duration: 4000 });
      }
    } catch (err) {
      toast({ title: 'Upload error', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleBioChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxBioChars) setBio(text);
  };

  const handleSaveBio = async () => {
    setIsSavingBio(true);
    try {
      const result = await dispatch(updateUserBio(bio));
      if (result.success) {
        toast({
          title: 'Bio updated',
          description: 'Your bio has been updated successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || 'Failed to update bio');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update your bio. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingBio(false);
    }
  };

  const handleLeaveAssembly = () => {
    if (password.length < 6 || confirmation !== 'LEAVE THE ASSEMBLY') {
      toast({
        title: 'Error',
        description: 'Please enter your password and type "LEAVE THE ASSEMBLY" to confirm.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    dispatch(deleteAccount(password, confirmation))
      .then(result => {
        if (result.success) {
          onCloseLeaveDialog();
          handleModalClose();
        } else {
          throw new Error(result.error || 'Failed to leave the assembly');
        }
      })
      .catch(error => {
        toast({
          title: 'Error',
          description: error.message || 'Something went wrong. Please try again.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        onOpen={handleModalOpen}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader borderBottomWidth="1px" borderColor="gray.700">
            Membership Settings
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            <Tabs isFitted variant="enclosed" colorScheme="blue">
              <TabList mb={4} borderColor="gray.600">
                <Tab _selected={{ color: 'white', bg: 'blue.700', borderColor: 'gray.600' }} color="gray.400">Profile</Tab>
                <Tab _selected={{ color: 'white', bg: 'blue.700', borderColor: 'gray.600' }} color="gray.400">Bio</Tab>
                <Tab _selected={{ color: 'white', bg: 'blue.700', borderColor: 'gray.600' }} color="gray.400">Avatar</Tab>
                <Tab _selected={{ color: 'white', bg: 'blue.700', borderColor: 'gray.600' }} color="gray.400">
                  <Icon as={FaShieldAlt} mr={1} boxSize={3} />Privacy
                </Tab>
                <Tab _selected={{ color: 'white', bg: 'red.800', borderColor: 'gray.600' }} color="gray.400">⚠️</Tab>
              </TabList>

              <TabPanels>
                {/* ── Tab 1: Profile ── */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel color="gray.300">Burrower ID</FormLabel>
                      <Input
                        value={profile.burrowerId || ''}
                        isReadOnly
                        bg="gray.700"
                        borderColor="gray.600"
                        color="white"
                        _readOnly={{ opacity: 0.8 }}
                      />
                      <FormHelperText color="gray.500">
                        Your unique identifier in the assembly.
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel color="gray.300">State Chat</FormLabel>
                      <HStack spacing={2}>
                        <Box flex="1">
                          <select
                            value={selectedState}
                            onChange={(e) => setSelectedState(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              backgroundColor: '#2D3748',
                              color: 'white',
                              border: '1px solid #4A5568',
                              borderRadius: '6px',
                              fontSize: '14px',
                              cursor: 'pointer'
                            }}
                          >
                            <option value="" style={{ backgroundColor: '#2D3748' }}>No state set</option>
                            {US_STATES.map(s => (
                              <option key={s.code} value={s.code} style={{ backgroundColor: '#2D3748' }}>
                                {s.name} ({s.code})
                              </option>
                            ))}
                          </select>
                        </Box>
                        <Button
                          size="sm"
                          colorScheme="green"
                          isLoading={isSavingState}
                          loadingText="Saving"
                          isDisabled={selectedState === (profile.userState || profile.state || '')}
                          onClick={async () => {
                            setIsSavingState(true);
                            try {
                              const result = await dispatch(updateUserState(selectedState || null));
                              if (result.success) {
                                toast({
                                  title: selectedState ? `State set to ${selectedState}` : 'State cleared',
                                  description: selectedState
                                    ? `You now have access to the ${selectedState} State Chat.`
                                    : 'State Chat access has been removed.',
                                  status: 'success',
                                  duration: 3000,
                                  isClosable: true,
                                });
                              } else {
                                throw new Error(result.error || 'Failed to update state');
                              }
                            } catch (error) {
                              toast({
                                title: 'Error',
                                description: error.message || 'Failed to update state.',
                                status: 'error',
                                duration: 4000,
                                isClosable: true,
                              });
                            } finally {
                              setIsSavingState(false);
                            }
                          }}
                        >
                          Save
                        </Button>
                      </HStack>
                      <FormHelperText color="gray.500">
                        {selectedState
                          ? `Your state is set to ${selectedState}. State Chat requires Supporter generosity or above.`
                          : 'Choose your state to connect with people in your area. State Chat requires Supporter generosity or above.'}
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel color="gray.300">Generosity Tier</FormLabel>
                      <Input
                        value={
                          profile.membershipTier === 'vip'         ? 'VIP'         :
                          profile.membershipTier === 'contributor' ? 'Contributor' :
                          profile.membershipTier === 'supporter'   ? 'Supporter'   : 'Base'
                        }
                        isReadOnly
                        bg="gray.700"
                        borderColor="gray.600"
                        color="white"
                        _readOnly={{ opacity: 0.8 }}
                      />
                      <FormHelperText color="gray.500">
                        Your current generosity tier.
                      </FormHelperText>
                    </FormControl>
                  </VStack>
                </TabPanel>

                {/* ── Tab 2: Bio ── */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="sm" color="gray.400">
                      This is what members see on your profile under "About Me."
                    </Text>

                    <FormControl>
                      <FormLabel color="gray.300">About Me</FormLabel>
                      <Textarea
                        value={bio}
                        onChange={handleBioChange}
                        placeholder="Tell others about yourself..."
                        resize="vertical"
                        rows={6}
                        bg="gray.700"
                        borderColor="gray.600"
                        color="white"
                        _placeholder={{ color: 'gray.500' }}
                        _focus={{ borderColor: 'blue.400' }}
                        onFocus={() => {
                          if (!bioInitialized) {
                            setBio(profile.userBio || '');
                            setBioInitialized(true);
                          }
                        }}
                      />
                      <Flex justify="space-between" mt={1}>
                        <Text fontSize="xs" color="gray.500">
                          Shown in the "About Me" section of your profile.
                        </Text>
                        <Text
                          fontSize="xs"
                          color={bio.length > maxBioChars * 0.8 ? 'orange.400' : 'gray.500'}
                        >
                          {bio.length}/{maxBioChars}
                        </Text>
                      </Flex>
                    </FormControl>

                    {/* Live preview */}
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.400" mb={2}>
                        Preview
                      </Text>
                      <Box
                        bg="gray.700"
                        borderRadius="md"
                        p={3}
                        minHeight="60px"
                        borderWidth="1px"
                        borderColor="gray.600"
                      >
                        <Text fontSize="sm" color={bio ? 'white' : 'gray.500'}>
                          {bio || 'Your bio will appear here...'}
                        </Text>
                      </Box>
                    </Box>

                    <Button
                      colorScheme="blue"
                      onClick={handleSaveBio}
                      isLoading={isSavingBio || profileLoading}
                      loadingText="Saving..."
                      alignSelf="flex-end"
                    >
                      Save Bio
                    </Button>
                  </VStack>
                </TabPanel>

                {/* ── Tab 3: Avatar ── */}
                <TabPanel px={0}>
                  <VStack spacing={6} align="center">
                    <Text fontSize="sm" color="gray.400" alignSelf="flex-start">
                      Your current avatar. Click the button below to choose a new one.
                    </Text>

                    <Box
                      p={3}
                      borderRadius="xl"
                      borderWidth="2px"
                      borderColor="yellow.400"
                      bg="gray.700"
                    >
                      <Image
                        src={profile.userAvatar || 'https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png'}
                        alt={profile.burrowerId || 'Avatar'}
                        borderRadius="lg"
                        boxSize="160px"
                        objectFit="cover"
                        fallbackSrc="https://bunny-burrow-pull-1.b-cdn.net/user-profile-images/image%20(16).png"
                      />
                    </Box>

                    <Button
                      leftIcon={<Icon as={FaUser} />}
                      colorScheme="teal"
                      onClick={avatarSelector.onOpen}
                    >
                      Choose from Gallery
                    </Button>

                    {/* ── VIP Custom Profile Image ── */}
                    <Divider borderColor="gray.600" />

                    <Box w="100%" position="relative">
                      {/* Lock overlay for non-VIP */}
                      {!isVip && !isAdmin && (
                        <Box
                          position="absolute"
                          inset={0}
                          bg="blackAlpha.600"
                          borderRadius="lg"
                          zIndex={2}
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          backdropFilter="blur(2px)"
                        >
                          <Icon as={FaLock} color="gray.400" boxSize={6} mb={2} />
                          <Text color="gray.300" fontSize="sm" fontWeight="bold">
                            VIP Exclusive
                          </Text>
                          <Text color="gray.500" fontSize="xs" mt={1}>
                            Reach VIP tier to upload a custom profile image
                          </Text>
                        </Box>
                      )}

                      <VStack
                        spacing={4}
                        p={4}
                        bg="gray.750"
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor={isVip || isAdmin ? 'yellow.600' : 'gray.600'}
                        opacity={isVip || isAdmin ? 1 : 0.5}
                      >
                        <HStack spacing={2} alignSelf="flex-start">
                          <Icon as={FaCrown} color="yellow.400" />
                          <Text fontSize="sm" fontWeight="bold" color="yellow.400">
                            Custom Profile Image
                          </Text>
                          <Badge colorScheme="yellow" fontSize="xs">VIP</Badge>
                        </HStack>

                        <Text fontSize="xs" color="gray.400" alignSelf="flex-start">
                          Upload your own image as your profile picture. Replaces any previous custom upload. JPEG, PNG, WebP, or GIF — max 2 MB.
                        </Text>

                        {/* Hidden file input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          style={{ display: 'none' }}
                          onChange={handleAvatarFileSelect}
                        />

                        {/* ── Crop Modal ── */}
                        {showCropper && cropImageSrc && (
                          <Modal isOpen={showCropper} onClose={handleCropCancel} size="lg" isCentered>
                            <ModalOverlay bg="blackAlpha.800" />
                            <ModalContent bg="gray.800" mx={4}>
                              <ModalHeader color="gray.100" fontSize="md">
                                <HStack spacing={2}>
                                  <Icon as={FaCropAlt} color="yellow.400" />
                                  <Text>Position Your Avatar</Text>
                                </HStack>
                              </ModalHeader>
                              <ModalCloseButton color="gray.400" />
                              <ModalBody pb={2}>
                                <Text fontSize="xs" color="gray.400" mb={3}>
                                  Drag to reposition. Use the slider to zoom in or out.
                                </Text>
                                <Box
                                  position="relative"
                                  w="100%"
                                  h="300px"
                                  borderRadius="lg"
                                  overflow="hidden"
                                  bg="gray.900"
                                >
                                  <Cropper
                                    image={cropImageSrc}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    cropShape="round"
                                    showGrid={false}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                  />
                                </Box>
                                <HStack spacing={3} mt={4} px={2}>
                                  <Icon as={FaSearchPlus} color="gray.400" boxSize={3} />
                                  <Slider
                                    aria-label="Zoom"
                                    min={1}
                                    max={3}
                                    step={0.05}
                                    value={zoom}
                                    onChange={setZoom}
                                    colorScheme="yellow"
                                    flex={1}
                                  >
                                    <SliderTrack bg="gray.600">
                                      <SliderFilledTrack />
                                    </SliderTrack>
                                    <SliderThumb boxSize={4} />
                                  </Slider>
                                  <Text fontSize="xs" color="gray.500" minW="35px">
                                    {zoom.toFixed(1)}x
                                  </Text>
                                </HStack>
                              </ModalBody>
                              <ModalFooter>
                                <Button variant="ghost" colorScheme="gray" size="sm" mr={3} onClick={handleCropCancel}>
                                  Cancel
                                </Button>
                                <Button colorScheme="yellow" size="sm" onClick={handleCropConfirm}>
                                  Confirm Crop
                                </Button>
                              </ModalFooter>
                            </ModalContent>
                          </Modal>
                        )}

                        {/* Preview of cropped file */}
                        {customAvatarPreview && (
                          <VStack spacing={2}>
                            <Box
                              p={2}
                              borderRadius="full"
                              borderWidth="2px"
                              borderColor="yellow.400"
                              bg="gray.700"
                            >
                              <Image
                                src={customAvatarPreview}
                                alt="Cropped preview"
                                borderRadius="full"
                                boxSize="120px"
                                objectFit="cover"
                              />
                            </Box>
                            <Text fontSize="xs" color="gray.500">Cropped preview</Text>
                          </VStack>
                        )}

                        {isUploadingAvatar && (
                          <Progress size="sm" isIndeterminate colorScheme="yellow" w="100%" borderRadius="full" />
                        )}

                        <HStack spacing={3}>
                          <Button
                            leftIcon={<Icon as={FaUpload} />}
                            variant="outline"
                            colorScheme="yellow"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            isDisabled={isUploadingAvatar || (!isVip && !isAdmin)}
                          >
                            {customAvatarFile ? 'Choose Different' : 'Select Image'}
                          </Button>

                          {customAvatarFile && (
                            <Button
                              colorScheme="yellow"
                              size="sm"
                              onClick={handleUploadCustomAvatar}
                              isLoading={isUploadingAvatar}
                              loadingText="Uploading..."
                            >
                              Upload & Set
                            </Button>
                          )}
                        </HStack>

                        {customAvatarFile && !isUploadingAvatar && (
                          <Text fontSize="xs" color="gray.500">
                            Ready to upload ({(customAvatarFile.size / 1024).toFixed(0)} KB)
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  </VStack>
                </TabPanel>

                {/* ── Tab 4: Privacy (Phase 6) ── */}
                <TabPanel px={0}>
                  {privacyLoading ? (
                    <VStack py={8}><Spinner color="blue.400" /><Text fontSize="sm" color="gray.400">Loading privacy settings...</Text></VStack>
                  ) : (
                    <VStack spacing={5} align="stretch">
                      <Text fontSize="sm" color="gray.400">
                        Control who can see parts of your profile and who can message you.
                      </Text>

                      <FormControl>
                        <FormLabel color="gray.300" fontSize="sm">Profile Visibility</FormLabel>
                        <Select
                          value={privacySettings.profileVisibility}
                          onChange={(e) => setPrivacySettings(prev => ({ ...prev, profileVisibility: e.target.value }))}
                          bg="gray.700" borderColor="gray.600" color="white" size="sm"
                        >
                          <option value="public" style={{ backgroundColor: '#2D3748' }}>Public — Anyone</option>
                          <option value="community" style={{ backgroundColor: '#2D3748' }}>Community — Logged-in members</option>
                          <option value="supporting" style={{ backgroundColor: '#2D3748' }}>Supporting — Supporters & above</option>
                          <option value="kin_only" style={{ backgroundColor: '#2D3748' }}>Kin Only — Your kin list</option>
                        </Select>
                        <FormHelperText color="gray.500" fontSize="xs">Who can view your profile page</FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel color="gray.300" fontSize="sm">Bio Visibility</FormLabel>
                        <Select
                          value={privacySettings.bioVisibility}
                          onChange={(e) => setPrivacySettings(prev => ({ ...prev, bioVisibility: e.target.value }))}
                          bg="gray.700" borderColor="gray.600" color="white" size="sm"
                        >
                          <option value="public" style={{ backgroundColor: '#2D3748' }}>Public — Anyone</option>
                          <option value="community" style={{ backgroundColor: '#2D3748' }}>Community — Logged-in members</option>
                          <option value="supporting" style={{ backgroundColor: '#2D3748' }}>Supporting — Supporters & above</option>
                          <option value="kin_only" style={{ backgroundColor: '#2D3748' }}>Kin Only — Your kin list</option>
                        </Select>
                        <FormHelperText color="gray.500" fontSize="xs">Who can see your bio / About Me</FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel color="gray.300" fontSize="sm">Online Status Visibility</FormLabel>
                        <Select
                          value={privacySettings.onlineStatusVisibility}
                          onChange={(e) => setPrivacySettings(prev => ({ ...prev, onlineStatusVisibility: e.target.value }))}
                          bg="gray.700" borderColor="gray.600" color="white" size="sm"
                        >
                          <option value="public" style={{ backgroundColor: '#2D3748' }}>Public — Anyone</option>
                          <option value="community" style={{ backgroundColor: '#2D3748' }}>Community — Logged-in members</option>
                          <option value="supporting" style={{ backgroundColor: '#2D3748' }}>Supporting — Supporters & above</option>
                          <option value="kin_only" style={{ backgroundColor: '#2D3748' }}>Kin Only — Your kin list</option>
                        </Select>
                        <FormHelperText color="gray.500" fontSize="xs">Who can see when you're online</FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel color="gray.300" fontSize="sm">Kin List Visibility</FormLabel>
                        <Select
                          value={privacySettings.kinListVisibility}
                          onChange={(e) => setPrivacySettings(prev => ({ ...prev, kinListVisibility: e.target.value }))}
                          bg="gray.700" borderColor="gray.600" color="white" size="sm"
                        >
                          <option value="public" style={{ backgroundColor: '#2D3748' }}>Public — Anyone</option>
                          <option value="community" style={{ backgroundColor: '#2D3748' }}>Community — Logged-in members</option>
                          <option value="supporting" style={{ backgroundColor: '#2D3748' }}>Supporting — Supporters & above</option>
                          <option value="kin_only" style={{ backgroundColor: '#2D3748' }}>Kin Only — Your kin list</option>
                        </Select>
                        <FormHelperText color="gray.500" fontSize="xs">Who can see your kin connections</FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel color="gray.300" fontSize="sm">Messaging Gate</FormLabel>
                        <Select
                          value={privacySettings.messagingGate}
                          onChange={(e) => setPrivacySettings(prev => ({ ...prev, messagingGate: e.target.value }))}
                          bg="gray.700" borderColor="gray.600" color="white" size="sm"
                        >
                          <option value="public" style={{ backgroundColor: '#2D3748' }}>Public — Anyone can message</option>
                          <option value="community" style={{ backgroundColor: '#2D3748' }}>Community — Logged-in members</option>
                          <option value="supporting" style={{ backgroundColor: '#2D3748' }}>Supporting — Supporters & above</option>
                          <option value="kin_only" style={{ backgroundColor: '#2D3748' }}>Kin Only — Only your kin</option>
                        </Select>
                        <FormHelperText color="gray.500" fontSize="xs">Who can send you private messages</FormHelperText>
                      </FormControl>

                      <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={handleSavePrivacy}
                        isLoading={privacySaving}
                        loadingText="Saving..."
                        alignSelf="flex-end"
                      >
                        Save Privacy Settings
                      </Button>

                      {/* ── Blocked Users ── */}
                      <Divider borderColor="gray.600" />
                      <Box>
                        <HStack mb={2}>
                          <Icon as={FaBan} color="red.400" boxSize={3} />
                          <Text fontSize="sm" fontWeight="bold" color="gray.300">Blocked Users</Text>
                        </HStack>
                        {blockedUsers.length === 0 ? (
                          <Text fontSize="xs" color="gray.500">You haven't blocked anyone.</Text>
                        ) : (
                          <VStack spacing={2} align="stretch">
                            {blockedUsers.map(userId => (
                              <HStack key={userId} bg="gray.700" px={3} py={2} borderRadius="md" justify="space-between">
                                <Text fontSize="sm" color="gray.200">{userId}</Text>
                                <Tooltip label="Unblock" placement="left">
                                  <IconButton
                                    icon={<FaTimesCircle />}
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    onClick={() => handleUnblock(userId)}
                                    aria-label={`Unblock ${userId}`}
                                  />
                                </Tooltip>
                              </HStack>
                            ))}
                          </VStack>
                        )}
                      </Box>
                    </VStack>
                  )}
                </TabPanel>

                {/* ── Tab 5: Danger Zone ── */}
                <TabPanel px={0}>
                  <VStack spacing={5} align="stretch">
                    <Alert status="warning" borderRadius="md" bg="red.900" borderWidth="1px" borderColor="red.600">
                      <AlertIcon color="red.300" />
                      <Text fontSize="sm" color="red.100">
                        When you leave, your data stays with the assembly per the Member Agreement.
                        This cannot be undone.
                      </Text>
                    </Alert>

                    <Divider borderColor="gray.600" />

                    <Box>
                      <Text fontWeight="bold" color="red.300" mb={1}>
                        Leave the Assembly
                      </Text>
                      <Text fontSize="sm" color="gray.400" mb={4}>
                        Deactivates your membership and cancels any recurring contributions.
                        Your Member Units, contribution history, and assembly records are
                        permanently retained by the assembly. All contributions are non-refundable.
                      </Text>
                      <Text fontSize="xs" color="gray.500" mb={4}>
                        If you wish to have personal profile data (bio, avatar) removed after
                        leaving, you must submit a written request. The assembly retains all
                        transaction and usage records indefinitely for legal defense.
                      </Text>

                      <Button
                        leftIcon={<Icon as={FaExclamationTriangle} />}
                        colorScheme="red"
                        variant="outline"
                        onClick={onOpenLeaveDialog}
                        borderWidth="2px"
                      >
                        Leave the Assembly
                      </Button>
                    </Box>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" borderColor="gray.700">
            <Button variant="ghost" color="gray.400" onClick={handleModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Avatar Selector (triggered from Avatar tab) */}
      <AvatarSelector
        isOpen={avatarSelector.isOpen}
        onClose={avatarSelector.onClose}
        currentAvatar={profile.userAvatar}
      />

      {/* Leave the Assembly Confirmation Dialog */}
      <AlertDialog
        isOpen={isLeaveDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseLeaveDialog}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              color="red.400"
              borderBottomWidth="1px"
              borderColor="gray.700"
            >
              Leave the Assembly
            </AlertDialogHeader>

            <AlertDialogBody py={5}>
              <VStack spacing={4} align="stretch">
                <Text>
                  This will deactivate your membership. Your recurring contributions
                  will be cancelled. Any remaining Member Units will be returned to
                  the assembly.
                </Text>

                <Alert status="error" borderRadius="md" bg="red.900" borderWidth="1px" borderColor="red.600">
                  <AlertIcon color="red.300" />
                  <Text fontSize="sm" color="red.100">
                    Your contribution history and assembly records will be retained
                    per the Member Agreement. This action cannot be undone.
                  </Text>
                </Alert>

                <Text fontSize="xs" color="gray.400">
                  If you wish to have your personal information removed, you must
                  submit a written request after leaving. The assembly has 90 days
                  to process such requests.
                </Text>

                <Divider borderColor="gray.600" />

                <FormControl>
                  <FormLabel color="gray.300">Enter your password</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    bg="gray.700"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color="gray.300">
                    Type <Text as="span" color="red.300" fontFamily="mono">LEAVE THE ASSEMBLY</Text> to confirm
                  </FormLabel>
                  <Input
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    placeholder="LEAVE THE ASSEMBLY"
                    bg="gray.700"
                    borderColor="gray.600"
                    color="white"
                    _placeholder={{ color: 'gray.500' }}
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter borderTopWidth="1px" borderColor="gray.700">
              <Button ref={cancelRef} onClick={onCloseLeaveDialog} variant="ghost" color="gray.400">
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleLeaveAssembly}
                ml={3}
                isLoading={profileLoading}
                loadingText="Processing..."
                isDisabled={password.length < 6 || confirmation !== 'LEAVE THE ASSEMBLY'}
              >
                Leave the Assembly
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default MembershipSettings;
